/**
 * SavedViewsPanel.jsx
 * ────────────────────
 * Slide-in drawer for saving, loading, and deleting personal grid views.
 * A view captures columnVisibility + sortKeys configuration.
 * Stored in localStorage only — no backend.
 */

import React, { useState } from 'react';
import { LIFECYCLE_COLUMNS } from './lifecycleColumnManifest';

function countVisibleColumns(columnVisibility, columns) {
  return columns.filter(c => columnVisibility[c.key] !== false).length;
}

export default function SavedViewsPanel({
  screen,
  callerEmail,
  currentColumnVisibility,
  currentSortKeys,
  savedViews,
  onSaveView,
  onLoadView,
  onDeleteView,
  onClose,
  columns = LIFECYCLE_COLUMNS,
}) {
  /* ── Save new view form state ───────────────────────────────────────── */
  const [showSaveForm, setShowSaveForm] = useState(false);
  const [viewName, setViewName] = useState('');
  const [saveError, setSaveError] = useState(null);

  /* ── Inline delete confirmation per row ─────────────────────────────── */
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  const visibleCount = countVisibleColumns(currentColumnVisibility, columns);
  const sortCount = (currentSortKeys || []).length;

  function handleSave() {
    const result = onSaveView(viewName);
    if (result?.error) {
      setSaveError(result.error);
      return;
    }
    setViewName('');
    setShowSaveForm(false);
    setSaveError(null);
  }

  function handleCancelSave() {
    setViewName('');
    setShowSaveForm(false);
    setSaveError(null);
  }

  function handleConfirmDelete(id) {
    onDeleteView(id);
    setConfirmDeleteId(null);
  }

  function handleKeyDown(e) {
    if (e.key === 'Enter') { e.preventDefault(); handleSave(); }
    if (e.key === 'Escape') { e.preventDefault(); handleCancelSave(); }
  }

  return (
    <div className="gv-col-panel-overlay" onClick={onClose}>
      <div className="gv-col-panel gv-views-panel" onClick={e => e.stopPropagation()}>
        {/* ── Header ────────────────────────────────────────────────────── */}
        <div className="gv-col-panel-header">
          <h3 className="gv-col-panel-title">Saved Views</h3>
          <button type="button" className="gv-col-panel-close" onClick={onClose}>&times;</button>
        </div>

        {/* ── Current config summary + save ──────────────────────────── */}
        <div className="gv-views-current">
          <div className="gv-views-current-summary">
            {visibleCount} column{visibleCount !== 1 ? 's' : ''} visible, {sortCount} sort level{sortCount !== 1 ? 's' : ''}
          </div>
          {!showSaveForm ? (
            <button type="button" className="lrc-btn gv-views-save-btn"
              onClick={() => { setShowSaveForm(true); setSaveError(null); }}>
              Save current view
            </button>
          ) : (
            <div className="gv-views-save-form">
              <input
                type="text"
                className="lrc-input gv-views-name-input"
                placeholder="View name..."
                value={viewName}
                onChange={e => { setViewName(e.target.value); setSaveError(null); }}
                onKeyDown={handleKeyDown}
                autoFocus
              />
              <button type="button" className="gv-views-save-confirm" onClick={handleSave}
                disabled={!viewName.trim()}>
                &#10003;
              </button>
              <button type="button" className="gv-views-save-cancel" onClick={handleCancelSave}>
                &#10005;
              </button>
              {saveError && <div className="gv-views-save-error">{saveError}</div>}
            </div>
          )}
        </div>

        {/* ── Views list ────────────────────────────────────────────────── */}
        <div className="gv-views-list">
          {savedViews.length === 0 ? (
            <div className="gv-views-empty">
              No saved views yet. Configure columns and sort, then save your current view.
            </div>
          ) : (
            savedViews.map(view => {
              const vCols = view.columnVisibility
                ? countVisibleColumns(view.columnVisibility, columns)
                : '?';
              const vSort = (view.sortKeys || []).length;
              const isDeleting = confirmDeleteId === view.id;

              return (
                <div key={view.id} className="gv-views-row">
                  <div className="gv-views-row-info">
                    <span className="gv-views-row-name">{view.name}</span>
                    <span className="gv-views-row-meta">
                      {vCols} column{vCols !== 1 ? 's' : ''} &middot; {vSort} sort{vSort !== 1 ? 's' : ''}
                    </span>
                  </div>
                  {!isDeleting ? (
                    <div className="gv-views-row-actions">
                      <button type="button" className="gv-views-load-btn"
                        onClick={() => { onLoadView(view); onClose(); }}>
                        Load
                      </button>
                      <button type="button" className="gv-views-delete-btn"
                        onClick={() => setConfirmDeleteId(view.id)}>
                        Delete
                      </button>
                    </div>
                  ) : (
                    <div className="gv-views-delete-confirm">
                      <span className="gv-views-delete-prompt">Delete this view?</span>
                      <button type="button" className="gv-views-delete-yes"
                        onClick={() => handleConfirmDelete(view.id)}>
                        Yes
                      </button>
                      <button type="button" className="gv-views-delete-no"
                        onClick={() => setConfirmDeleteId(null)}>
                        No
                      </button>
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
}
